import { IGetPendingInvitationResponse, IGetTeamMembersResponse } from '@/services/types/Business';
import { GetCardResponse, RsCardSpendLimit, SpendLimitTypeEnum } from '@/services/types/Card';
import { EnumValues } from '@/services/types/EnumValues';
import {
  MemberAccountStatusEnum,
  TAccessLevel,
  TMemberAccountStatus,
  TTeamMemberRow
} from '../types/TeamMember';
import { formatMemberPhoneNumber } from './formatMemberPhoneNumber';
import { getTeamMemberDisplayName } from './teamMemberDisplayName';

// CardState values that still count towards a member's card total - a deleted/expired card was
// never "issued" from the admin's point of view, so it's excluded rather than counted as frozen.
// Exported so the selection-actions hook can target the same set of cards when freezing/removing.
export const COUNTABLE_CARD_STATES = new Set(['created', 'active']);

/**
 * cardId -> whether that card currently carries an "all" type rule (see GET /cards/{cardId}/rules
 * - useTeamMembers fetches this per card, since GET /cards itself exposes no frozen flag). A card
 * missing from this map is treated as active - it means the rules fetch for it hasn't
 * loaded/failed, which should read as "not known to be frozen" rather than silently frozen.
 */
export type TCardFrozenMap = Record<string, boolean>;

/** The one limit shown on a card's own list row (Figma's "Limit $X.XX" pill) - a card missing
 *  from this map either has no limit set, or its limits fetch hasn't loaded/failed yet, same
 *  "absence isn't a claim" convention as TCardFrozenMap. */
export type TCardPrimaryLimit = {
  type: EnumValues<typeof SpendLimitTypeEnum>;
  totalAmount: number;
};
export type TCardLimitMap = Record<string, TCardPrimaryLimit | undefined>;

// The backend doesn't enforce recurring-vs-total exclusivity (see CardSettingsModal's own note on
// AddCardSpendLimitsUseCase), so a card can technically carry more than one limit type at once -
// this decides which single one the list row shows. TOTAL is the headline "spend up to $X in
// total" figure, so it wins over a recurring cadence; among the recurring types, the shortest
// period is shown first since it's the most immediately binding constraint on the card.
const LIMIT_DISPLAY_PRIORITY: EnumValues<typeof SpendLimitTypeEnum>[] = [
  SpendLimitTypeEnum.TOTAL,
  SpendLimitTypeEnum.DAILY,
  SpendLimitTypeEnum.WEEKLY,
  SpendLimitTypeEnum.MONTHLY
];

export const pickPrimaryCardLimit = (
  limits: RsCardSpendLimit[] | undefined
): TCardPrimaryLimit | undefined => {
  if (!limits || limits.length === 0) return undefined;
  for (const type of LIMIT_DISPLAY_PRIORITY) {
    const match = limits.find((limit) => limit.type === type);
    if (match) return { type: match.type, totalAmount: match.totalAmount };
  }
  return undefined;
};

const countMemberCards = (
  customerId: string,
  cards: GetCardResponse[],
  frozenByCardId: TCardFrozenMap
): { activeCardCount: number; frozenCardCount: number } => {
  const memberCards = cards.filter(
    (card) =>
      card.cardholderId === customerId && COUNTABLE_CARD_STATES.has(card.state.toLowerCase())
  );

  const frozenCardCount = memberCards.filter((card) => frozenByCardId[card.id]).length;

  return { activeCardCount: memberCards.length - frozenCardCount, frozenCardCount };
};

export const buildTeamMemberRows = (
  members: IGetTeamMembersResponse[],
  cards: GetCardResponse[],
  frozenByCardId: TCardFrozenMap
): TTeamMemberRow[] =>
  members
    // Inactive means removed/no-longer-has-access, per the backend's AccountStatus - this list is
    // for managing who currently has (or is getting) access, not a historical record of everyone
    // who ever did. Pending/Paused stay visible (each with its own status badge) - only Inactive
    // is excluded.
    .filter((member) => member.accountStatus !== MemberAccountStatusEnum.INACTIVE)
    .map((member) => {
      const { displayName, legalName } = getTeamMemberDisplayName(
        member.firstName,
        member.lastName,
        member.preferredName
      );
      const accessLevel = member.accessLevel as TAccessLevel;
      const accountStatus = member.accountStatus as TMemberAccountStatus;
      const phoneDisplay = formatMemberPhoneNumber(member.countryCode, member.phoneNumber);
      const { activeCardCount, frozenCardCount } = countMemberCards(
        member.customerId,
        cards,
        frozenByCardId
      );

      return {
        customerId: member.customerId,
        displayName,
        legalName,
        phoneDisplay,
        email: member.email,
        accessLevel,
        accountStatus,
        displayPictureId: member.displayPictureId,
        activeCardCount,
        frozenCardCount,
        searchText: [displayName, legalName, member.phoneNumber, phoneDisplay, member.email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
      };
    });

/**
 * A not-yet-accepted invite (GET .../pending-invitations, roles=employee) reshaped into the same
 * row shape the table already renders - accountStatus is always PENDING (reusing the existing
 * badge/eligibility handling for that status, see TTeamMemberRow's own doc comment) and card
 * counts are always 0 (an unaccepted invite has no account to issue a card against yet).
 */
export const buildPendingInviteRows = (
  invitations: IGetPendingInvitationResponse[]
): TTeamMemberRow[] =>
  invitations
    .filter((invitation) => invitation.status === MemberAccountStatusEnum.PENDING)
    .map((invitation) => {
      const { displayName, legalName } = getTeamMemberDisplayName(
        invitation.firstName,
        invitation.lastName ?? ''
      );
      const accessLevel = (invitation.accessLevel ?? 'team_member') as TAccessLevel;
      const phoneDisplay = invitation.countryCode
        ? formatMemberPhoneNumber(invitation.countryCode, invitation.phoneNumber ?? '')
        : '';

      return {
        customerId: `invite:${invitation.id}`,
        displayName,
        legalName,
        phoneDisplay,
        email: invitation.email ?? '',
        accessLevel,
        accountStatus: MemberAccountStatusEnum.PENDING,
        activeCardCount: 0,
        frozenCardCount: 0,
        searchText: [displayName, legalName, invitation.phoneNumber, phoneDisplay, invitation.email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
        pendingInvitationId: invitation.id
      };
    });
